import React, { Fragment, memo, useCallback, useEffect, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { DeleteDocusignTemplateModal } from 'src/components/docusign-template/delete/DeleteDocusignTemplateModal'
import { DocusignTemplateFormModal } from 'src/components/docusign-template/docusign-template-form/DocusignTemplateFormModal'
import type { DocusignTemplateFormValues } from 'src/components/docusign-template/docusign-template-form/DocusignTemplateForm.interface'
import { DocusignTemplatesTable } from 'src/components/docusign-template/docusign-templates-table/DocusignTemplatesTable'
import { DocusignTemplatesTableToolbar } from 'src/components/docusign-template/docusign-templates-table/DocusignTemplatesTableToolbar'
import { Button } from 'src/components/shared/Button'
import {
  AdminCreateDocusignTemplate,
  AdminDeleteDocusignTemplate,
  AdminFetchDocusignTemplates,
} from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { DocusignTemplate } from 'src/interfaces/docusignTemplate'
import {
  getDocusignTemplateDeletingId,
  getDocusignTemplateSaving,
} from 'src/selectors/docusignTemplate'
import { blankToUndefined } from 'src/utils/blankToUndefined'
import { GSIcon } from 'src/components/shared/GSIcon'

const CustomerDocusignPageComponent = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const [searchQuery, setSearchQuery] = useState<string>('')
  // Bumped by the Refresh button and by a successful create or delete — included in the fetch
  // effect's dependency array below so any of them re-triggers the request.
  const [refreshToken, setRefreshToken] = useState<number>(0)
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false)
  const [templatePendingDelete, setTemplatePendingDelete] = useState<DocusignTemplate | null>(null)

  const { isAuthenticated } = useAuthSession()
  const isSaving = useSelector(getDocusignTemplateSaving)
  const deletingId = useSelector(getDocusignTemplateDeletingId)
  // Add and Delete both call `docusign_templates_manage` endpoints.
  const canManage = useHasPermission(InternalPermission.DocusignTemplatesManage)

  /** The pending row's own in-flight state — drives both the dismissal guard and the modal. */
  const isDeletingPendingRow =
    templatePendingDelete !== null && deletingId === templatePendingDelete.id

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  // The single fetch effect: the endpoint takes no search or pagination params, so this only
  // reruns on a customer change, an auth change, or a manual refresh — not on the search term.
  useEffect(() => {
    if (!isAuthenticated || !customerId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchDocusignTemplates.request({ partnerId: customerId }))
  }, [asyncDispatchWithErrorToast, isAuthenticated, customerId, refreshToken])

  const handleOpenCreate = useCallback(() => {
    setIsFormOpen(true)
  }, [])

  // Guarded on `isSaving`, which covers every dismissal path at once — Cancel, the modal's close
  // button, and a backdrop click. Without it an admin could dismiss a saving form and have the
  // request's success handler close a dialog they had since reopened, discarding their input.
  const handleCloseFormModal = useCallback(() => {
    if (isSaving) {
      return
    }

    setIsFormOpen(false)
  }, [isSaving])

  const handleOpenDelete = useCallback((template: DocusignTemplate) => {
    setTemplatePendingDelete(template)
  }, [])

  // Guarded the same way, and this is also what keeps `deletingId` correct as a single value
  // rather than a set: only one row can be pending deletion at a time, and while its request is
  // in flight the modal cannot be dismissed — so a second delete cannot be started, and one
  // completing request can never clear another's in-flight state.
  const handleCloseDeleteModal = useCallback(() => {
    if (isDeletingPendingRow) {
      return
    }

    setTemplatePendingDelete(null)
  }, [isDeletingPendingRow])

  const handleSubmit = useCallback(
    async (values: DocusignTemplateFormValues) => {
      if (!customerId) {
        return
      }

      const created = await asyncDispatchWithErrorToast<DocusignTemplate>(
        AdminCreateDocusignTemplate.request({
          partnerId: customerId,
          // `docusignId` is the create DTO's field name for what the response calls
          // `docusignTemplateId` — see the design's §2.2.
          docusignId: values.docusignId.trim(),
          title: values.title.trim(),
          description: blankToUndefined(values.description),
        }),
      )

      if (!created) {
        return
      }

      snackbar?.enqueueSnackbar('Template created', { variant: 'success' })
      setIsFormOpen(false)
      handleRefresh()
    },
    [customerId, asyncDispatchWithErrorToast, snackbar, handleRefresh],
  )

  const handleConfirmDelete = useCallback(async () => {
    if (!templatePendingDelete) {
      return
    }

    // A delete rejected by the insurance-policy-product foreign key resolves to null here, and
    // the error toast carrying the backend's own message has already been raised — the modal
    // stays open so the admin can read it against the row they picked. See the design's §2.5.
    const deleted = await asyncDispatchWithErrorToast<string>(
      AdminDeleteDocusignTemplate.request({ templateId: templatePendingDelete.id }),
    )

    if (!deleted) {
      return
    }

    snackbar?.enqueueSnackbar('Template deleted', { variant: 'success' })
    setTemplatePendingDelete(null)
    handleRefresh()
  }, [templatePendingDelete, asyncDispatchWithErrorToast, snackbar, handleRefresh])

  return (
    <Fragment>
      <div data-testid="admin-customer-docusign-page" css={tw`min-w-0 flex-1 p-6`}>
        <div css={tw`mb-5 flex flex-row items-start justify-between gap-4`}>
          <div>
            <Typography variant="h2">Docusign</Typography>
          </div>
          {canManage ? (
            <Button
              size="small"
              color="dark"
              leftIcon={<GSIcon iconName="gs_plus_medium" size={20} css={tw`text-shades-0`} />}
              onClick={handleOpenCreate}
              data-testid="docusign-templates-list-add-button"
            >
              Add template
            </Button>
          ) : null}
        </div>
        <DocusignTemplatesTableToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onRefresh={handleRefresh}
        />
        <DocusignTemplatesTable
          searchQuery={searchQuery}
          canManage={canManage}
          onDeleteTemplate={handleOpenDelete}
        />
      </div>
      <DocusignTemplateFormModal
        isOpen={isFormOpen}
        isSaving={isSaving}
        onSubmit={handleSubmit}
        onClose={handleCloseFormModal}
      />
      <DeleteDocusignTemplateModal
        isOpen={Boolean(templatePendingDelete)}
        template={templatePendingDelete}
        isDeleting={isDeletingPendingRow}
        onConfirm={handleConfirmDelete}
        onClose={handleCloseDeleteModal}
      />
    </Fragment>
  )
}

export const CustomerDocusignPage = memo(CustomerDocusignPageComponent)
